import { OAuthPopup } from "@cbar/oauth-popup";
import {
    LOGIN_STATUS_MESSAGES,
    buildAuthStartUrl,
} from "./auth-login-status";

const LOGIN_NAVIGATE_DELAY_MS = 50;

const isInIframe = (): boolean => {
    try {
        return window.self !== window.top;
    } catch (e) {
        return true;
    }
};

/**
 * When this page is loaded inside a popup/iframe handoff, tell the parent
 * whether auth succeeded or failed. Prefer #redirect_url over location.href.
 */
export const notifySilentLoginParent = () => {
    if (!isInIframe()) {
        return;
    }

    const isLoginPage = window.location.pathname.indexOf("/auth/login") === 0;
    const authFailed = !!document.querySelector("#auth_failed");
    const oauthError = document.querySelector("#oauth_error") as HTMLInputElement | null;
    const redirectInput = document.querySelector("#redirect_url") as HTMLInputElement | null;

    if (isLoginPage || authFailed || (oauthError && oauthError.value)) {
        window.parent.postMessage(
            {
                source: "cbar-login",
                message: "error",
                error: oauthError && oauthError.value ? oauthError.value : "User is not logged in",
            },
            "*"
        );
        return;
    }

    if (redirectInput && redirectInput.value) {
        window.parent.postMessage(
            {
                source: "cbar-login",
                message: "success",
                redirectUrl: redirectInput.value,
            },
            "*"
        );
    }
};

const isOAuthCallbackPath = (): boolean => {
    const path = window.location.pathname;
    return path.indexOf("/auth/social") === 0 || path.indexOf("/auth/login") === 0;
};

export const setupOAuthPopup = () => {
    // Only finish OAuth handoff on auth routes. Other pages historically reused
    // id="redirect_url" (e.g. upload-files modal), which caused same-URL
    // location.replace loops on invoices.
    if (isOAuthCallbackPath()
        && (document.querySelector("#redirect_url") || document.querySelector("#auth_failed"))) {
        return finishOAuthCallbackPage();
    }

    if (isOAuthCallbackPath() && OAuthPopup.isPopupWindow()) {
        return handlePopupCallback();
    }

    if (isInIframe()) {
        return;
    }

    const loginButtons = document.querySelectorAll(".authentickated-login");
    const isLoginPage = loginButtons.length > 0;

    if (!isLoginPage) {
        return;
    }

    const registerButton = document.querySelector("#register-button") as HTMLElement | null;
    const spinner = getLoginSpinner();
    let loginStarted = false;

    // Do not auto-start silent SSO on page load (that redirect chain looped).
    // Log In uses interactive OAuth: Authentickated reuses an existing session
    // or shows the sign-in form. The status text must not claim a sign-in
    // redirect before we know which of those will happen.
    showLogin(loginButtons, registerButton, spinner);

    const startInteractiveLogin = (_button: HTMLButtonElement, register: boolean) => {
        if (loginStarted) {
            return;
        }
        loginStarted = true;

        hideLoginControls(loginButtons, registerButton);

        if (register) {
            setLoadingState(LOGIN_STATUS_MESSAGES.redirectSignUp, true);
            navigateTo(buildAuthStartUrl({ register: true }));
            return;
        }

        setLoadingState(LOGIN_STATUS_MESSAGES.checking, true);
        navigateTo(buildAuthStartUrl());
    };

    const handler = function (this: HTMLButtonElement, event: Event, register = false) {
        const e = event as MouseEvent;
        e.preventDefault();
        startInteractiveLogin(this, register);
    };

    loginButtons.forEach((button) => {
        const loginButton = button as HTMLButtonElement;
        loginButton.addEventListener("click", handler.bind(loginButton));
    });

    if (registerButton) {
        registerButton.addEventListener("click", (e) => {
            handler.call(registerButton as HTMLButtonElement, e, true);
        });
    }
};

export const handlePopupCallback = () => {
    return finishOAuthCallbackPage();
};

/**
 * Complete /auth/social for popup, iframe, or top-level window.
 */
const finishOAuthCallbackPage = () => {
    const path = window.location.pathname;
    const isLoginPage = path.indexOf("/auth/login") === 0;
    const authFailed = !!document.querySelector("#auth_failed");
    const redirectInput = document.querySelector("#redirect_url") as HTMLInputElement | null;
    const redirectUrl = redirectInput && redirectInput.value ? redirectInput.value : null;

    if (isLoginPage) {
        try {
            window.close();
        } catch (e) {
            // ignore
        }
        return;
    }

    if (isInIframe()) {
        return;
    }

    if (authFailed || !redirectUrl) {
        if (OAuthPopup.isPopupWindow()) {
            try {
                window.close();
            } catch (e) {
                // ignore
            }
        }
        return;
    }

    if (OAuthPopup.isPopupWindow()) {
        const notified = OAuthPopup.notifyOpener({ redirectUrl });
        if (notified) {
            return;
        }

        // Cross-scheme opener (http callback / https parent): postMessage, then close.
        // Never navigate the popup to the app URL — that caused invoice redirect loops.
        try {
            window.opener?.postMessage(
                { source: 'cbar-oauth-popup', message: 'success', redirectUrl },
                '*'
            );
            window.close();
            return;
        } catch (e) {
            // Fall through to same-window navigation.
        }
    }

    // Top-level / full-page OAuth — continue here.
    // Never bounce back into auth endpoints (that restarts silent/login loops).
    try {
        const target = new URL(redirectUrl, window.location.origin);
        if (
            target.pathname.indexOf('/auth/login') === 0
            || target.pathname.indexOf('/auth/social') === 0
            || target.pathname.indexOf('/auth/logout') === 0
        ) {
            window.location.replace('/account/index');
            return;
        }

        // Same-path replace is a full reload and can loop forever if this
        // handoff runs on a non-auth page that still has #redirect_url.
        if (target.pathname === window.location.pathname) {
            return;
        }
    } catch (e) {
        // Fall through to replace with the raw value.
    }

    window.location.replace(redirectUrl);
};

const getLoginStatusEl = (): HTMLElement | null => {
    return document.querySelector("#loading-indicator");
};

const getLoginSpinner = (): HTMLElement | null => {
    return document.querySelector("#login-status-spinner")
        ?? document.querySelector("cbar-circular-progress");
};

const setLoadingState = (message: string, showSpinner: boolean) => {
    const statusEl = getLoginStatusEl();
    if (statusEl) {
        statusEl.textContent = message;
    }

    const spinner = getLoginSpinner();
    if (spinner) {
        spinner.hidden = !showSpinner;
    }
};

const hideLoginControls = (
    loginButtons: NodeListOf<Element>,
    registerButton: HTMLElement | null
) => {
    loginButtons.forEach((btn) => {
        (btn as HTMLElement).hidden = true;
    });
    if (registerButton) {
        registerButton.hidden = true;
    }
};

const showLogin = (
    loginButtons: NodeListOf<Element>,
    registerButton: HTMLElement | null,
    spinner: HTMLElement | null
) => {
    loginButtons.forEach((btn) => {
        (btn as HTMLElement).hidden = false;
    });

    if (registerButton) {
        registerButton.hidden = false;
    }

    const statusEl = getLoginStatusEl();
    if (statusEl) {
        statusEl.textContent = "";
    }

    if (spinner) {
        spinner.hidden = true;
    }
};

const navigateTo = (url: string) => {
    window.setTimeout(() => {
        window.location.assign(url);
    }, LOGIN_NAVIGATE_DELAY_MS);
};
